import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme as useAppTheme } from '../../../context/ThemeContext';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useApi } from 'context/ApiProvider';
import toast from 'react-hot-toast';
import { useDesignStore } from 'context/DesignContext';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { GCodeSetting, applyGCodeSettingDefaults } from 'models/GcodeSetting';
import { Hardness } from 'components/Viewer3d/consts';
import { Printer } from 'models/Printer';
import { fetchAllPrinterHelpers, fetchAllPrinters, fetchAllPrinterSettings, restoreDefaultPrinters, restorePrintHelpers, saveDefaultPrinters, savePrinterHelper, storeDefaultPrinters, storePrintHelpers } from 'services/Printerservice';
import { ConditionSide } from 'models/Insole';
import { getPrintersByUserIdList } from 'services/adminService';
import { useAuth } from 'context/AuthContext';
import { PrinterSetting } from 'models/PrinterSetting';
import { PrinterHelpertSetting } from 'models/PrinterHelperSetting';
import { createPrinter, getPrinterTypeByUserGroup, PrinterType } from 'Printers/PrinterFactory';


interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface PrinterRow {
  printerId: string;
  name: string;
  ip: string;
  settingId: string;
  settingName: string;
  defaultPrinter: ConditionSide;
  userId: string;
  isNew: boolean;
  isUpdated: boolean;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useAppTheme();
  const createEmptyRow = (): PrinterRow => ({ printerId: '', name: '', ip: '', settingId: '', settingName: '', defaultPrinter: ConditionSide.none, userId: '', isNew: true, isUpdated: false });

  const [printers, setPrinters] = useState([createEmptyRow()]);
  const [printerErrors, setPrinterErrors] = useState<{ index: number; field: string }[]>([]);
  const [availablePrinterSettings, setAvailablePrinterSettings] = useState<{ name: string, id: string }[]>([]);
  const api = useApi();
  const [connectionResult, setConnectionResult] = useState<{
    ip: string;
    success: boolean;
    message: string;
  } | null>(null);
  const [leftInsolePrinterId, setLeftInsolePrinterId] = useState('');
  const [rightInsolePrinterId, setRightInsolePrinterId] = useState('');
  const [printerHelperRight, setPrinterHelperRight] = useState<PrinterHelpertSetting | undefined>(new PrinterHelpertSetting());
  const [printerHelperLeft, setPrinterHelperLeft] = useState<PrinterHelpertSetting | undefined>(new PrinterHelpertSetting());
  const authenticatedUser = useAuth().user;
  const isHubMember = !authenticatedUser?.isAdmin && !!authenticatedUser?.group?.IsHub;
  const sectionBorderColor = isDarkMode ? 'rgba(255,255,255,0.18)' : '#ccc';
  const sectionHeaderSx = {
    backgroundColor: isDarkMode ? '#3a3a3a' : '#f0f0f0',
    color: isDarkMode ? '#fff' : '#000',
    p: 1.2,
    borderBottom: `1px solid ${sectionBorderColor}`,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  };
  const voiceTouchedRef = useRef(false);
  const dataReadyRef = useRef(false);
  const pendingVoiceRef = useRef<Record<string, unknown> | null>(null);
  const pendingGetRef = useRef(false);
  const printersRef = useRef<PrinterRow[]>([]);
  const settingsListRef = useRef<{ name: string, id: string }[]>([]);


  const handleClose = () => {
    setPrinterErrors([]);
    onClose();
  };

  const handleOk = () => {
    syncPrinters().then((success) => {
      savePrinterHelper(api, printerHelperRight!);
      savePrinterHelper(api, printerHelperLeft!);
      storePrintHelpers(printerHelperRight!, printerHelperLeft!);
      if (success) {
        handleClose();
      }
    });
  }

  const handleChange = (index: number, field: string, value: string, section: string) => {
    const update = (prev: PrinterRow[]) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (!updated[index].isNew)
        updated[index].isUpdated = true;
      return updated;
    };

    if (section === 'printers') {
      setPrinterErrors((prev) =>
        prev.filter((err) => !(err.index === index && err.field === field))
      );
    }

    if (section === 'printers') setPrinters(update);
  };

  const fetchAvailablePrinters = async (): Promise<Printer[]> => {
    const currentDesignCreatorId = useSelectedDesignStore.getState().createdBy?.id;
    if (
      authenticatedUser?.isAdmin &&
      currentDesignCreatorId
    ) {
      return getPrintersByUserIdList(api, [authenticatedUser.id, currentDesignCreatorId]);
      // return getPrintersByUserIdList(api, [authenticatedUser.id]);
    }
    return fetchAllPrinters(api);
  }

  const fetchData = async () => {
    try {
      const settings = await fetchAllPrinterSettings(api);
      const settingSelectItems: { name: string, id: string }[] = settings.map((s: PrinterSetting) => { return { name: s.name, id: s.id.toString() } });
      setAvailablePrinterSettings(settingSelectItems);
      const printersInDb = await fetchAvailablePrinters();
      const mappedPrinters = printersInDb.map(p => ({
        printerId: p.printerId.toString(),
        name: p.name,
        ip: p.ip,
        settingId: p.setting.id.toString() ?? '',
        settingName: p.setting.name ?? '',
        defaultPrinter: p.defaultPrinter,
        userId: p.userId.toString(),
        isNew: false,
        isUpdated: false,
      }));

      const nextPrinters = mappedPrinters.length > 0 ? mappedPrinters : [createEmptyRow()];
      settingsListRef.current = settingSelectItems;
      if (!voiceTouchedRef.current) {
        printersRef.current = nextPrinters;
        setPrinters(nextPrinters);
      }

      const helpers = await fetchAllPrinterHelpers(api);
      if (!voiceTouchedRef.current && helpers != null && helpers.length > 0) {
        setPrinterHelperRight(helpers.find(a => a.side.toLocaleLowerCase() === 'right'));
        setPrinterHelperLeft(helpers.find(a => a.side.toLocaleLowerCase() === 'left'));
      }
      setDefaultPrinterSetting(settingSelectItems);
      return mappedPrinters
    } catch (error) {
      console.error('Error fetching printers or settings:', error);
      setPrinters([createEmptyRow()]);
      return [];
    }
  };

  const validateSelectedInsolePrinters = async () => {
    var updatedPrinterList = printers;
    var updatedLeftInsolePrinterId = leftInsolePrinterId;
    var updatedRightInsolePrinterId = rightInsolePrinterId;
    // Whether the panel is in a position to say anything about the defaults at
    // all. Both dropdowns empty is not the user clearing them: it is what the
    // panel looks like before the defaults have been loaded into it, which
    // happens whenever no design is open. Pressing OK in that state used to
    // store two empty entries over the real configuration, and the assistant
    // then reported that no default printer was set up.
    const hadSelection = updatedLeftInsolePrinterId !== '' || updatedRightInsolePrinterId !== '';
    var isSelectedIdentical = updatedLeftInsolePrinterId === updatedRightInsolePrinterId;
    // Add settings for newly added printers that did not exist in db
    if (updatedLeftInsolePrinterId !== '') {
      var newPrinter = updatedPrinterList.find(p => p.isNew && p.name === updatedLeftInsolePrinterId);
      const leftGcodeSetting = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() === 'left');
      if (newPrinter) {
        updatedPrinterList = await fetchData();
        newPrinter = updatedPrinterList.find(p => p.name === updatedLeftInsolePrinterId);
        const newPrinterForRightFoot = updatedPrinterList.find(p => p.name === updatedRightInsolePrinterId);
        if (newPrinterForRightFoot) {
          updatedRightInsolePrinterId = newPrinterForRightFoot.printerId;
        }
        if (newPrinter) {
          if (leftGcodeSetting) {
            leftGcodeSetting.printerId = newPrinter.printerId;
            updateGcodeSettingStore(leftGcodeSetting);
          }
          setLeftInsolePrinterId(newPrinter.printerId);
          updatedLeftInsolePrinterId = newPrinter.printerId;
        }
      } else {
        if (leftGcodeSetting) {
          leftGcodeSetting.printerId = updatedLeftInsolePrinterId;
          updateGcodeSettingStore(leftGcodeSetting);
        }
        setLeftInsolePrinterId(updatedLeftInsolePrinterId);
        updatedLeftInsolePrinterId = updatedLeftInsolePrinterId;
      }
    }
    if (updatedRightInsolePrinterId !== '') {
      var newPrinter = updatedPrinterList.find(p => p.isNew && p.name === updatedRightInsolePrinterId);
      const rightGcodeSetting = useDesignStore.getState().gcodeSetting.find(g => g.side.toLowerCase() === 'right');
      if (isSelectedIdentical) {
        if (rightGcodeSetting) {
          rightGcodeSetting.printerId = updatedLeftInsolePrinterId;
          updateGcodeSettingStore(rightGcodeSetting);
        }
        setRightInsolePrinterId(updatedLeftInsolePrinterId);
        updatedRightInsolePrinterId = updatedLeftInsolePrinterId;
      } else if (newPrinter) {
        updatedPrinterList = await fetchData();
        newPrinter = updatedPrinterList.find(p => p.name === updatedRightInsolePrinterId);
        if (newPrinter) {
          if (rightGcodeSetting) {
            rightGcodeSetting.printerId = newPrinter.printerId;
            updateGcodeSettingStore(rightGcodeSetting);
          }
          setRightInsolePrinterId(newPrinter.printerId);
          updatedRightInsolePrinterId = newPrinter.printerId;
        }
      } else {
        if (rightGcodeSetting) {
          rightGcodeSetting.printerId = updatedRightInsolePrinterId;
          updateGcodeSettingStore(rightGcodeSetting);
        }
        setRightInsolePrinterId(updatedRightInsolePrinterId);
        updatedRightInsolePrinterId = updatedRightInsolePrinterId;
      }
    }
    // Deselect the default printers if they no longer exist
    const leftInsolePrinterObject = updatedPrinterList.find(p => p.printerId === updatedLeftInsolePrinterId.toString());
    const rightInsolePrinterObject = updatedPrinterList.find(p => p.printerId === updatedRightInsolePrinterId.toString());
    if (!leftInsolePrinterObject) {
      setLeftInsolePrinterId('');
      updatedLeftInsolePrinterId = '';
    }
    if (!rightInsolePrinterObject) {
      setRightInsolePrinterId('');
      updatedRightInsolePrinterId = '';
    }
    // Save the default printers. A deselection is still saved — a printer that
    // was removed has to stop being the default — but a panel that never had a
    // selection to begin with leaves the stored configuration alone.
    if (hadSelection) {
      storeDefaultPrinters(ConvertPrinterRoWToPrinter(leftInsolePrinterObject), ConvertPrinterRoWToPrinter(rightInsolePrinterObject));
    }
    if (
      leftInsolePrinterObject &&
      rightInsolePrinterObject &&
      (!authenticatedUser?.isAdmin || leftInsolePrinterObject.userId === authenticatedUser.id.toString())
    ) {
      await saveDefaultPrinters(api, parseInt(leftInsolePrinterObject.printerId), parseInt(rightInsolePrinterObject.printerId));
    }
  }

  const ConvertPrinterRoWToPrinter = (row: PrinterRow | undefined): Printer | undefined => {
    if (row === undefined) return undefined;
    return {
      printerId: row.printerId,
      name: row.name,
      ip: row.ip,
      defaultPrinter: row.defaultPrinter,
      userId: row.userId,
      setting: {
        id: Number(row.settingId), // اگر settingId string است
        name: row.settingName,
      },
    };
  }

  const syncPrinters = async () => {
    const newPrinterErrors: { index: number; field: string }[] = [];
    printers.forEach((printer, i) => {
      if (!printer.name.trim()) newPrinterErrors.push({ index: i, field: 'name' });
      if (!printer.ip.trim()) newPrinterErrors.push({ index: i, field: 'ip' });
      if (!printer.settingId.toString()) newPrinterErrors.push({ index: i, field: 'settingId' });
    });

    if (newPrinterErrors.length > 0) {
      setPrinterErrors(newPrinterErrors);
      return false;
    }
    try {
      const settingsList = await fetchAllPrinterSettings(api);
      const settingMap = new Map<number, string>();
      settingsList.forEach(s => settingMap.set(s.id, s.name));

      const printersInDb: Printer[] = await fetchAvailablePrinters();

      var newPrinters = printers.filter((p) => p.isNew) ?? [];
      const updatedPrinters = printers.filter(np => np.isUpdated) ?? [];
      const removedPrinters = printersInDb.filter((db) =>
        !printers.some((p) => p.printerId === db.printerId.toString()));

      for (const printer of newPrinters) {
        const savePrinterRes = await api.post('/api/printers', {
          name: printer.name,
          ip: printer.ip,
          settingId: printer.settingId,
        });
        if (!savePrinterRes.ok) {
          toast.error(`Failed to save changes. Please try again.`, { duration: 10000 });
          return false;
        }
      }

      for (const printer of updatedPrinters) {
        const savePrinterRes = await api.patch('/api/printers', {
          printerId: printer.printerId,
          name: printer.name,
          ip: printer.ip,
          settingId: printer.settingId,
        });
        if (!savePrinterRes.ok) {
          toast.error(`Failed to update some printers. Please try again.`, { duration: 10000 });
          return false;
        }
      }

      for (const printer of removedPrinters) {
        await api.delete(`/api/printers/${printer.printerId}`);
      }

      console.log('Printers synced successfully');
      toast.success(`New settings were saved successfully.`, { duration: 5000 });
      await validateSelectedInsolePrinters();
      return true;
    } catch (error) {
      console.error('Error syncing printers:', error);
      toast.error(`Failed to save changes. Please try again.`, { duration: 10000 });
      return false;
    }
  };

  const setDefaultPrinterHelpers = () => {
    if( printerHelperLeft?.userId!= 0 || printerHelperRight?.userId!=0) return;
    if (printerHelperLeft) {
      printerHelperLeft.setSide('left');
      printerHelperLeft.setUserId(authenticatedUser?.id!);
    }
    if (printerHelperRight) {
      printerHelperRight.setSide('right');
      printerHelperRight.setUserId(authenticatedUser?.id!);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      dataReadyRef.current = false;
      pendingVoiceRef.current = null;
      pendingGetRef.current = false;
      return;
    }
    voiceTouchedRef.current = false;
    dataReadyRef.current = false;
    setDefaultPrinterHelpers();
    fetchData().then((mappedApiPrinters) => {
      // Not conditional on a design being open any more. This only fills the
      // two Default Printer dropdowns from the stored configuration and the
      // server's own flags — neither of which belongs to a design — so gating it
      // meant the printers simply looked unconfigured whenever Settings was
      // opened with nothing loaded.
      loadDefaultPrinters(mappedApiPrinters);
      dataReadyRef.current = true;
      document.dispatchEvent(new Event("voice:settingsReady"));
      document.dispatchEvent(new Event("voice:settingsFlush"));
      if (pendingGetRef.current) {
        pendingGetRef.current = false;
        document.dispatchEvent(new Event("voice:getSettingsState"));
      }
    })
    const storedHelpers = restorePrintHelpers();
    if (!voiceTouchedRef.current && storedHelpers != null && storedHelpers.length > 0 && storedHelpers[0] != null) {
      const right = storedHelpers.find(h => h?.side?.toLowerCase() === 'right');
      const left = storedHelpers.find(h => h?.side?.toLowerCase() === 'left');

      if (right) setPrinterHelperRight(right);
      if (left) setPrinterHelperLeft(left);
    }
  }, [isOpen, api]);

  const setDefaultPrinterSetting = (settingSelectItems: { name: string, id: string }[] = []) => {
    let currentAvailablePrinterSettings = settingSelectItems.length > 0 ? settingSelectItems : availablePrinterSettings;
    if (currentAvailablePrinterSettings && currentAvailablePrinterSettings.length === 1) {
      printers.forEach((value, index) => {
        handleChange(index, 'settingId', currentAvailablePrinterSettings[0].id, 'printers');
      });
    }
  }

  /**
   * The id these two dropdowns select by, always as a string.
   *
   * Every row in the printer list carries printerId as a string, because that
   * is what a Select compares its value against. What comes back out of the
   * stored configuration does not: the server sends PrinterId as a number, and
   * the two places that store it straight from the server — the login page and
   * the exhibition entry — pass that number through. A number matched no row,
   * so a printer that was configured came up blank.
   *
   * Coerced here on the way in, and deliberately not on the way out to storage:
   * the print path reads that same stored copy and sends printerId on to the
   * server as it finds it.
   */
  const selectablePrinterId = (printer: { printerId?: string | number } | null | undefined): string =>
    printer?.printerId === undefined || printer?.printerId === null ? '' : String(printer.printerId);

  const loadDefaultPrinters = async (printerList: PrinterRow[] = []) => {
    if (!printerList || printerList.length === 0) printerList = printers;
    const { leftPrinterObject, rightPrinterObject } = restoreDefaultPrinters();

    // The stored choice wins, but only while it still names a printer on the
    // list. A default left behind by a printer that has since been renamed away
    // or deleted selected nothing, which looked exactly like no default at all;
    // the server's own flags are the better answer in that case.
    const pick = (stored: unknown, side: ConditionSide): string => {
      const storedId = selectablePrinterId(stored as { printerId?: string | number } | null);
      if (storedId && printerList.some(p => p.printerId === storedId)) return storedId;
      return selectablePrinterId(
        printerList.find(p => p.defaultPrinter === side || p.defaultPrinter === ConditionSide.both)
      );
    };

    setLeftInsolePrinterId(pick(leftPrinterObject, ConditionSide.left));
    setRightInsolePrinterId(pick(rightPrinterObject, ConditionSide.right));
  };

  const updateGcodeSettingStore = (gcodeSetting: GCodeSetting) => {
    gcodeSetting = applyGCodeSettingDefaults(gcodeSetting);
    const gcodeListClone = useDesignStore.getState().gcodeSetting.filter(g => g !== gcodeSetting);
    gcodeListClone.push(gcodeSetting);
    useDesignStore.setState({ gcodeSetting: gcodeListClone });
  }

  const newGCodeSetting = (side: string) => {
    return {
      checkToRemove: null,
      designId: useSelectedDesignStore.getState().designId,
      foreFootHardness: null,
      heelHardness: null,
      id: null,
      insoleHardness: Hardness.Medium,
      midInnerHardness: null,
      midOuterHardness: null,
      printerId: '',
      side: side
    } as GCodeSetting
  }

  const handleRightInsoleChanged = (printerId: string) => {
    setRightInsolePrinterId(printerId);
    var rightGCodeSettings = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() === 'right');
    if (rightGCodeSettings) {
      rightGCodeSettings.printerId = printerId;
    } else {
      rightGCodeSettings = newGCodeSetting('right');
      rightGCodeSettings.printerId = printerId;
    }
    updateGcodeSettingStore(rightGCodeSettings);
  }

  const handleLeftInsoleChanged = (printerId: string) => {
    setLeftInsolePrinterId(printerId);
    var leftGCodeSettings = useDesignStore.getState().gcodeSetting.find(a => a.side.toLowerCase() === 'left');
    if (leftGCodeSettings) {
      leftGCodeSettings.printerId = printerId;
    } else {
      leftGCodeSettings = newGCodeSetting('left');
      leftGCodeSettings.printerId = printerId;
    }
    updateGcodeSettingStore(leftGCodeSettings);
  }

  const handleLeftHeleperChange = (event: SelectChangeEvent) => {
    const value = event.target.value;

    setPrinterHelperLeft(prev => {
      const updated = new PrinterHelpertSetting();

      if (prev) {
        Object.assign(updated, prev);
      }

      updated.hasRamp = value === 'hasRamp';
      updated.cutFront = value === 'cutFront';

      return updated;
    });
  };


  const handleRightHeleperChange = (event: SelectChangeEvent) => {
    const value = event.target.value;

    setPrinterHelperRight(prev => {
      const updated = new PrinterHelpertSetting();

      if (prev) {
        Object.assign(updated, prev);
      }

      updated.hasRamp = value === 'hasRamp';
      updated.cutFront = value === 'cutFront';

      return updated;
    });
  };

  const checkPrinterConnectionHandler = async (ip: string, settingId: string) => {
    // const printerHandler = createPrinter(getPrinterTypeByUserGroup(authenticatedUser?.group));
    const selectedSetting = availablePrinterSettings.find(a=> a.id == settingId);
    if(selectedSetting== null) {console.log('no printer settings found'); return;};
    const printerHandler = createPrinter(selectedSetting.name as PrinterType);
    printerHandler.checkPrinterConnection(ip).then((result) => setConnectionResult(result))
  };

  const addEmptyPrinterRow = () => {
    const newPrinterRow = createEmptyRow();
    if (availablePrinterSettings && availablePrinterSettings.length === 1) {
      newPrinterRow.settingId = availablePrinterSettings[0].id;
    }
    setPrinters(prev => [...prev, newPrinterRow]);
  }

  const leftIdRef = useRef(leftInsolePrinterId);
  const rightIdRef = useRef(rightInsolePrinterId);
  const helperLeftRef = useRef(printerHelperLeft);
  const helperRightRef = useRef(printerHelperRight);
  const handleOkRef = useRef(handleOk);
  const handleLeftInsoleChangedRef = useRef(handleLeftInsoleChanged);
  const handleRightInsoleChangedRef = useRef(handleRightInsoleChanged);
  const checkConnectionRef = useRef(checkPrinterConnectionHandler);
  const connectionResultRef = useRef(connectionResult);
  printersRef.current = printers;
  leftIdRef.current = leftInsolePrinterId;
  rightIdRef.current = rightInsolePrinterId;
  helperLeftRef.current = printerHelperLeft;
  helperRightRef.current = printerHelperRight;
  handleOkRef.current = handleOk;
  settingsListRef.current = availablePrinterSettings;
  handleLeftInsoleChangedRef.current = handleLeftInsoleChanged;
  handleRightInsoleChangedRef.current = handleRightInsoleChanged;
  checkConnectionRef.current = checkPrinterConnectionHandler;
  connectionResultRef.current = connectionResult;

  const helperMode = (helper?: PrinterHelpertSetting) => {
    if (!helper) return "none";
    if (helper.hasRamp) return "ramp";
    if (helper.cutFront) return "frontTrim";
    return "none";
  };

  const helperSelectValue = (helper?: PrinterHelpertSetting) => {
    if (helper?.hasRamp) return "hasRamp";
    if (helper?.cutFront) return "cutFront";
    return "noSupport";
  };

  const printerNameForId = (id: string) => {
    const match = printersRef.current.find((p) => (p.printerId || p.name) === id);
    return match?.name || null;
  };

  const compactText = (value: string) => value.trim().toLowerCase().replace(/[\s_-]/g, "");

  const matchPrinterIndex = (list: PrinterRow[], query?: string) => {
    if (!query) {
      const filled = list.map((p, i) => ({ p, i })).filter(({ p }) => p.name && p.ip);
      if (filled.length === 1) return filled[0].i;
      return list.findIndex((p) => !p.name && !p.ip);
    }
    const compact = compactText(query);
    const exact = list.findIndex((p) => compactText(p.name || "") === compact);
    if (exact >= 0) return exact;
    const queryLower = query.trim().toLowerCase();
    return list.findIndex((p) => (p.name || "").toLowerCase().includes(queryLower));
  };

  const matchSetting = (query?: string) => {
    const list = settingsListRef.current;
    if (!list.length) return null;
    if (!query) return list.length === 1 ? list[0] : null;
    const compact = compactText(query);
    const aliases: Record<string, string> = {
      tsoles: "pro",
      pro: "pro",
      podo: "sole",
      sole: "sole",
      ideaformer: "lite",
      lite: "lite",
    };
    const wanted = aliases[compact] || compact;
    return list.find((s) => compactText(s.name) === wanted)
      || list.find((s) => compactText(s.name).includes(wanted) || wanted.includes(compactText(s.name)))
      || list.find((s) => s.name.toLowerCase().includes(query.trim().toLowerCase()))
      || null;
  };

  const cloneHelper = (prev: PrinterHelpertSetting | undefined, side: "left" | "right") => {
    const updated = new PrinterHelpertSetting();
    if (prev) {
      updated.id = prev.id;
      updated.userId = prev.userId;
      updated.hasRamp = !!prev.hasRamp;
      updated.cutFront = !!prev.cutFront;
    }
    updated.side = side;
    return updated;
  };

  const applyHelper = (side: "left" | "right", mode: string) => {
    const compact = compactText(mode);
    const normalized = compact === "ramp" || compact === "hasramp" ? "ramp"
      : compact === "fronttrim" || compact === "cutfront" ? "frontTrim"
      : "none";
    const setter = side === "left" ? setPrinterHelperLeft : setPrinterHelperRight;
    setter((prev) => {
      const updated = cloneHelper(prev, side);
      updated.hasRamp = normalized === "ramp";
      updated.cutFront = normalized === "frontTrim";
      return updated;
    });
  };

  const applyDefaultPrinter = (side: "left" | "right", name: string): boolean => {
    const query = name.trim().toLowerCase();
    const compact = compactText(name);
    const list = printersRef.current.filter((p) => p.name && p.ip);
    const match = list.find((p) => compactText(p.name) === compact)
      || list.find((p) => p.name.toLowerCase().includes(query));
    if (!match) return false;
    const id = match.printerId || match.name;
    if (side === "left") handleLeftInsoleChangedRef.current(id);
    else handleRightInsoleChangedRef.current(id);
    return true;
  };

  const emitSettingsState = () => {
    document.dispatchEvent(new CustomEvent("voice:settingsState", {
      detail: {
        printers: printersRef.current
          .filter((p) => p.name)
          .map((p) => ({
            name: p.name,
            ip: p.ip,
            setting: settingsListRef.current.find((s) => s.id === p.settingId)?.name || p.settingName || "",
          })),
        printerSettings: settingsListRef.current.map((s) => s.name),
        defaultLeft: printerNameForId(leftIdRef.current),
        defaultRight: printerNameForId(rightIdRef.current),
        helperLeft: helperMode(helperLeftRef.current),
        helperRight: helperMode(helperRightRef.current),
        connectionDialogOpen: !!connectionResultRef.current,
        connectionSuccess: connectionResultRef.current?.success ?? null,
        connectionMessage: connectionResultRef.current?.message || null,
      },
    }));
  };

  const emitSettingsResult = (ok: boolean, message: string) => {
    document.dispatchEvent(new CustomEvent("voice:settingsResult", { detail: { ok, message } }));
  };

  const applyVoiceDetail = (detail: Record<string, any>): { ok: boolean; message: string } => {
    voiceTouchedRef.current = true;
    const notes: string[] = [];

    if (detail.addPrinter) {
      const title = String(detail.addPrinter.name || "").trim();
      const address = String(detail.addPrinter.ip || "").trim();
      if (!title) return { ok: false, message: "Provide a printer title." };
      if (!address) return { ok: false, message: "Provide the printer address / IP." };
      const setting = matchSetting(detail.addPrinter.setting) || settingsListRef.current[0] || null;
      setPrinters((prev) => {
        const next = [...prev];
        const emptyIndex = next.findIndex((p) => !p.name && !p.ip);
        const row: PrinterRow = {
          printerId: "",
          name: title,
          ip: address,
          settingId: setting?.id || "",
          settingName: setting?.name || "",
          defaultPrinter: ConditionSide.none,
          userId: "",
          isNew: true,
          isUpdated: false,
        };
        if (emptyIndex >= 0) next[emptyIndex] = row;
        else next.push(row);
        printersRef.current = next;
        return next;
      });
      notes.push(`Printer ${title} added at ${address}${setting ? ` with setting ${setting.name}` : ""}.`);
    }

    if (detail.name || detail.ip || detail.setting) {
      const list = printersRef.current;
      const index = matchPrinterIndex(list, detail.printer);
      if (index < 0) return { ok: false, message: "Could not find that printer. Call getSettingsState for names." };
      const setting = detail.setting ? matchSetting(String(detail.setting)) : null;
      if (detail.setting && !setting) {
        return { ok: false, message: `Unknown printer setting. Use one of: ${settingsListRef.current.map((s) => s.name).join(", ")}.` };
      }
      setPrinters((prev) => {
        const next = [...prev];
        const current = { ...next[index] };
        if (detail.name) current.name = String(detail.name).trim();
        if (detail.ip) current.ip = String(detail.ip).trim();
        if (setting) {
          current.settingId = setting.id;
          current.settingName = setting.name;
        }
        if (!current.isNew) current.isUpdated = true;
        next[index] = current;
        return next;
      });
      notes.push("Printer fields updated.");
    }

    if (detail.removePrinter) {
      const query = detail.removePrinter === true ? "" : String(detail.removePrinter).trim();
      const filled = printersRef.current
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.name);
      let index = -1;
      if (!query) {
        if (filled.length === 1) index = filled[0].i;
        else if (filled.length === 0) {
          return { ok: false, message: "There is no printer to remove." };
        } else {
          return { ok: false, message: `Which printer? ${filled.map(({ p }) => p.name).join(", ")}.` };
        }
      } else {
        index = matchPrinterIndex(printersRef.current, query);
        if (index < 0 || !printersRef.current[index]?.name) {
          return { ok: false, message: `Could not find printer "${query}". Current printers: ${filled.map(({ p }) => p.name).join(", ") || "none"}.` };
        }
      }
      const removed = printersRef.current[index];
      const removedName = removed?.name || query;
      const removedId = removed?.printerId || removed?.name;
      setPrinters((prev) => {
        const next = prev.filter((_, i) => i !== index);
        const fallback = next.length ? next : [{
          printerId: "",
          name: "",
          ip: "",
          settingId: settingsListRef.current[0]?.id ?? "",
          settingName: settingsListRef.current[0]?.name ?? "",
          defaultPrinter: ConditionSide.none,
          userId: "",
          isNew: true,
          isUpdated: false,
        }];
        printersRef.current = fallback;
        return fallback;
      });
      if (removedId && leftIdRef.current === removedId) handleLeftInsoleChangedRef.current("");
      if (removedId && rightIdRef.current === removedId) handleRightInsoleChangedRef.current("");
      notes.push(`Removed printer ${removedName}. Ask if they want to save. If they say yes, call saveSettings.`);
    }

    if (detail.closeConnection) {
      setConnectionResult(null);
      return { ok: true, message: "Connection result closed. Settings is still open." };
    }

    if (detail.checkConnection) {
      const index = matchPrinterIndex(printersRef.current, String(detail.checkConnection));
      if (index < 0) return { ok: false, message: "Could not find that printer to check." };
      const row = printersRef.current[index];
      if (!row.ip || !row.settingId) return { ok: false, message: "Printer needs an address and a setting before checking connection." };
      checkConnectionRef.current(row.ip, row.settingId);
      notes.push(`Checking connection for ${row.name}. A result dialog will open. When they say OK, close, or dismiss, call setSettingsParam with param=closeConnection. Do not save settings yet.`);
    }

    if (detail.defaultLeft) {
      if (!applyDefaultPrinter("left", String(detail.defaultLeft))) {
        return { ok: false, message: `No printer named ${detail.defaultLeft}.` };
      }
      notes.push(`Left default printer is ${detail.defaultLeft}.`);
    }
    if (detail.defaultRight) {
      if (!applyDefaultPrinter("right", String(detail.defaultRight))) {
        return { ok: false, message: `No printer named ${detail.defaultRight}.` };
      }
      notes.push(`Right default printer is ${detail.defaultRight}.`);
    }

    if ("helperLeft" in detail) {
      const mode = String(detail.helperLeft);
      applyHelper("left", mode);
      notes.push(`Left helper is ${compactText(mode) === "ramp" || compactText(mode) === "hasramp" ? "ramp" : compactText(mode) === "fronttrim" || compactText(mode) === "cutfront" ? "frontTrim" : "none"}.`);
    }
    if ("helperRight" in detail) {
      const mode = String(detail.helperRight);
      applyHelper("right", mode);
      notes.push(`Right helper is ${compactText(mode) === "ramp" || compactText(mode) === "hasramp" ? "ramp" : compactText(mode) === "fronttrim" || compactText(mode) === "cutfront" ? "frontTrim" : "none"}.`);
    }

    const askSave = !detail.checkConnection || notes.length > 1;
    const message = notes.filter(Boolean).join(" ") + (askSave && !detail.checkConnection ? " Ask if they want to save. If they say yes, call saveSettings." : "");
    return { ok: true, message: message.trim() || "Settings updated." };
  };

  useEffect(() => {
    const onGet = () => {
      if (!isOpen) return;
      if (!dataReadyRef.current) {
        pendingGetRef.current = true;
        return;
      }
      emitSettingsState();
    };
    const onPing = () => {
      document.dispatchEvent(new CustomEvent("voice:settingsStatus", {
        detail: { open: isOpen, ready: dataReadyRef.current },
      }));
      if (isOpen && dataReadyRef.current) {
        document.dispatchEvent(new Event("voice:settingsReady"));
        flushPendingVoice();
      }
    };
    const flushPendingVoice = () => {
      if (!isOpen || !dataReadyRef.current || !pendingVoiceRef.current) return;
      const queued = pendingVoiceRef.current;
      pendingVoiceRef.current = null;
      const result = applyVoiceDetail(queued);
      emitSettingsResult(result.ok, result.message);
    };
    const onSet = (event: Event) => {
      if (!isOpen) return;
      const detail = (event as CustomEvent).detail || {};
      if (!dataReadyRef.current) {
        pendingVoiceRef.current = detail;
        return;
      }
      const result = applyVoiceDetail(detail);
      emitSettingsResult(result.ok, result.message);
    };
    const onFlush = () => flushPendingVoice();
    const onSave = () => {
      if (!isOpen) return;
      if (connectionResultRef.current) {
        setConnectionResult(null);
        emitSettingsResult(true, "Connection result closed. Settings is still open.");
        return;
      }
      handleOkRef.current();
    };
    document.addEventListener("voice:getSettingsState", onGet);
    document.addEventListener("voice:pingSettings", onPing);
    document.addEventListener("voice:setSettings", onSet as EventListener);
    document.addEventListener("voice:settingsFlush", onFlush);
    document.addEventListener("voice:saveSettings", onSave);
    return () => {
      document.removeEventListener("voice:getSettingsState", onGet);
      document.removeEventListener("voice:pingSettings", onPing);
      document.removeEventListener("voice:setSettings", onSet as EventListener);
      document.removeEventListener("voice:settingsFlush", onFlush);
      document.removeEventListener("voice:saveSettings", onSave);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dataReadyRef.current) return;
    if (pendingGetRef.current) {
      pendingGetRef.current = false;
      emitSettingsState();
    }
    if (pendingVoiceRef.current) {
      const detail = pendingVoiceRef.current;
      pendingVoiceRef.current = null;
      const result = applyVoiceDetail(detail);
      emitSettingsResult(result.ok, result.message);
    }
  }, [isOpen, printers, availablePrinterSettings]);

  return (
    <>
      <Modal open={isOpen} onClose={handleClose} aria-labelledby="setting-title" closeAfterTransition sx={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
        <Box
          sx={{
            bgcolor: 'var(--bg-color)',
            color: isDarkMode ? '#fff' : '#000',
            borderRadius: 2,
            width: { xs: '95vw', md: '95vw', lg: '40vw' },
            maxHeight: { xs: '95vh', md: '95vh', lg: '80vh' },
            mx: 'auto',
            boxShadow: 24,
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            position: 'relative',
          }}
        >

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: `1px solid ${sectionBorderColor}` }}>
            <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
              Settings
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 3,
            }}
          >
            {isHubMember && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  bgcolor: isDarkMode ? 'rgba(33, 150, 243, 0.16)' : undefined,
                  color: isDarkMode ? '#e3f2fd' : undefined,
                  '& .MuiAlert-icon': {
                    color: isDarkMode ? '#90caf9' : undefined,
                  },
                }}
              >
                Your prints are sent to your hub administrator as a print request.
                After they confirm, they will print for you. You do not need to change
                these printer settings.
              </Alert>
            )}

            {/* Printer Section */}
            <Box sx={{ border: `1px solid ${sectionBorderColor}`, borderRadius: 2, mb: 2 }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Printers
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {printers.map((printer, i) => (
                  <Box key={i} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 3, flexWrap: 'wrap', justifyContent: 'space-between'}}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Printer Title"
                        value={printer.name}
                        onChange={(e) => handleChange(i, 'name', e.target.value, 'printers')}
                        error={printerErrors.some(err => err.index === i && err.field === 'name')}
                        sx={{
                          '& label': {
                            color: isDarkMode ? '#fff' : '#000', fontSize: '0.95rem'
                          },
                          '& .MuiInputBase-input': {
                            color: isDarkMode ? '#fff' : '#000', fontSize: '0.95rem',
                          },
                          '& fieldset': {
                            borderColor: 'var(--input-border)',
                          },
                          minWidth: 85,
                          flex: 1,
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Address"
                        value={printer.ip}
                        onChange={(e) => handleChange(i, 'ip', e.target.value, 'printers')}
                        error={printerErrors.some(err => err.index === i && err.field === 'ip')}
                        sx={{
                          '& label': {
                            color: isDarkMode ? '#fff' : '#000', fontSize: '0.95rem'
                          },
                          '& .MuiInputBase-input': {
                            color: isDarkMode ? '#fff' : '#000', fontSize: '0.95rem'
                          },
                          '& fieldset': {
                            borderColor: 'var(--input-border)',
                          },
                          minWidth: 85,
                          flex: 1,
                        }}
                      />
                      <Select
                        fullWidth
                        size="small"
                        value={printer.settingId}
                        displayEmpty
                        onChange={(e) => handleChange(i, 'settingId', e.target.value, 'printers')}
                        error={printerErrors.some(err => err.index === i && err.field === 'settingId')}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: isDarkMode ? '#fff' : '#000',
                            fontSize: '0.95rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                          },
                          '& fieldset': {
                            borderColor: 'var(--input-border)',
                          },
                          minWidth: 85,
                          flex: 1,
                        }}
                        name={printer.settingId.toString() || ''}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: '0.95rem' }}>
                          Select Setting
                        </MenuItem>
                        {availablePrinterSettings.map((setting) => (
                          <MenuItem key={setting.id} value={setting.id} sx={{
                            fontSize: '0.95rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '100%',
                          }}>
                            {setting.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ 
                          textTransform: 'none', 
                          borderColor: 'var(--input-border)',
                          minWidth: 85,
                          flex: 1, 
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                        }}
                        onClick={() => checkPrinterConnectionHandler(printer.ip, printer.settingId)}
                      >
                        Check Connection
                      </Button>
                      <IconButton
                        color="error"
                        onClick={() => {
                          const updated = [...printers];
                          updated.splice(i, 1);
                          setPrinters(updated);
                        }}
                        sx={{flex:1, flexGrow: 0}}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button variant="contained" fullWidth startIcon={<AddIcon />} sx={{ backgroundColor: '#076596', color: '#fff' }}
                    onClick={addEmptyPrinterRow}
                  >
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Insole Print Section */}
            <Box sx={{ border: `1px solid ${sectionBorderColor}`, borderRadius: 2, mb: 2 }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Default Printer
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1, gap: 2, p: 2 }}>
                {/* Left Insole */}
                <FormControl
                  sx={{
                    display: 'flex',
                    flex: 1,
                  }}
                >
                  <InputLabel id="left-insole-label" sx={{ color: isDarkMode ? '#fff' : '#000' }} size='small'>
                    Left
                  </InputLabel>
                  <Select
                    fullWidth
                    size="small"
                    value={leftInsolePrinterId}
                    onChange={(e) => handleLeftInsoleChanged(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: isDarkMode ? '#fff' : '#000',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      },
                      '& fieldset': {
                        borderColor: 'var(--input-border)',
                      }
                    }}
                    title={leftInsolePrinterId || ''}
                    label='Left'
                  >
                    {printers.filter(p => p.name && p.ip).length === 0 ? (
                      <MenuItem value="" disabled sx={{ fontSize: '0.95rem' }}>
                        No printers
                      </MenuItem>
                    ) : (
                      printers
                        .filter(p => p.name && p.ip)
                        .map(printer => (
                          <MenuItem
                            key={printer.printerId || printer.name}
                            value={printer.printerId || printer.name}
                            sx={{
                              fontSize: '0.95rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              maxWidth: '100%',
                            }}
                          >
                            {printer.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>

                {/* Right Insole */}
                <FormControl
                  sx={{
                    display: 'flex',
                    flex: 1,
                  }}
                >
                  <InputLabel id="right-insole-label" sx={{ color: isDarkMode ? '#fff' : '#000' }} size='small'>
                    Right
                  </InputLabel>
                  <Select
                    fullWidth
                    size="small"
                    value={rightInsolePrinterId}
                    onChange={(e) => handleRightInsoleChanged(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: isDarkMode ? '#fff' : '#000',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      },
                      '& fieldset': {
                        borderColor: 'var(--input-border)',
                      }
                    }}
                    title={rightInsolePrinterId || ''}
                    label='Right'
                  >
                    {printers.filter(p => p.name && p.ip).length === 0 ? (
                      <MenuItem value="" disabled sx={{ fontSize: '0.95rem' }}>
                        No printers
                      </MenuItem>
                    ) : (
                      printers
                        .filter(p => p.name && p.ip)
                        .map(printer => (
                          <MenuItem
                            key={printer.printerId || printer.name}
                            value={printer.printerId || printer.name}
                            sx={{
                              fontSize: '0.95rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              maxWidth: '100%',
                            }}
                          >
                            {printer.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>

              </Box>
            </Box>

            {/* Helper Section*/}
            <Box sx={{ border: `1px solid ${sectionBorderColor}`, borderRadius: 2 }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Helpers
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1, gap: 2, p: 2 }}>

                {/* Left Insole */}
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel
                    id="left-helper-label"
                    sx={{ color: isDarkMode ? '#fff' : '#000' }}
                    size="small"
                  >
                    Left
                  </InputLabel>

                  <Select
                    fullWidth
                    size="small"
                    value={helperSelectValue(printerHelperLeft)}
                    onChange={handleLeftHeleperChange}
                    label="Left"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: isDarkMode ? '#fff' : '#000',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      },
                      '& fieldset': {
                        borderColor: 'var(--input-border)',
                      }
                    }}
                  >
                    <MenuItem value="noSupport" sx={{
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '100%',
                    }}>No Support</MenuItem>

                    <MenuItem value="hasRamp" sx={{
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '100%',
                    }}>Ramp Support</MenuItem>

                    <MenuItem value="cutFront"
                      sx={{
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '100%',
                      }}>Front Trim</MenuItem>
                  </Select>
                </FormControl>


                {/* Right Insole */}
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel
                    id="right-helper-label"
                    sx={{ color: isDarkMode ? '#fff' : '#000' }}
                    size="small"
                  >
                    Right
                  </InputLabel>

                  <Select
                    fullWidth
                    size="small"
                    value={helperSelectValue(printerHelperRight)}
                    onChange={handleRightHeleperChange}
                    label="Right"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: isDarkMode ? '#fff' : '#000',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      },
                      '& fieldset': {
                        borderColor: 'var(--input-border)',
                      }
                    }}
                  >
                    <MenuItem value="noSupport" sx={{
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '100%',
                    }}>No Support</MenuItem>
                    
                    <MenuItem value="hasRamp" sx={{
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '100%',
                    }}>Ramp Support</MenuItem>

                    <MenuItem value="cutFront" sx={{
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      maxWidth: '100%',
                    }}>Front Trim</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: `1px solid ${sectionBorderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                color: isDarkMode ? '#fff' : '#000',
                borderColor: '#aaa',
                width: '80px',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOk}
              sx={{
                color: '#fff',
                borderColor: '#aaa',
                backgroundColor: '#076596',
                width: '80px',
                fontWeight: 500,
                px: 4,
                '&:hover': { backgroundColor: '#0a6fb4' }
              }}
            >
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>
      <Dialog
        open={!!connectionResult}
        onClose={() => setConnectionResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {connectionResult?.success
            ? 'Connection Successful'
            : 'Connection Failed'}
        </DialogTitle>

        <DialogContent dividers>

          {/* Message */}
          <Typography
            variant="body2"
            sx={{
              color: connectionResult?.success ? 'green' : 'error.main',
              mb: 1,
            }}
          >
            {connectionResult?.message}{' '}

            {!connectionResult?.success && connectionResult?.ip && (
              <a
                href={`https://${connectionResult.ip}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#0a6fb4',
                  textDecoration: 'underline',
                  wordBreak: 'break-word',
                }}
              >
                https://{connectionResult.ip}
              </a>
            )}
          </Typography>


          {/* Video Section */}
          {!connectionResult?.success && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: "center" }}>
                Manual Certificate Trust
              </Typography>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                }}
              >
                <video
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    borderRadius: 8,
                    width: '100%',
                    maxWidth: '100%',
                    height: 250,
                    objectFit: 'cover',
                  }}
                >
                  <source
                    src="/videos/certificate-help.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </Box>
            </Box>
          )}

        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConnectionResult(null)}
            variant="contained"
            sx={{
              color: '#fff',
              backgroundColor: '#076596',
              width: 80,
              fontWeight: 500,
              '&:hover': { backgroundColor: '#0a6fb4' }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default SettingsPanel;
